import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, CloudOff, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { listMine, retry, discard, runSyncPass } from "../../lib/opQueue";
import { Button } from "../../components/ui/button";

const STATUS_META = {
    pending: { color: "text-warning", icon: CloudOff, label: "wartet" },
    syncing: { color: "text-info", icon: Loader2, label: "lädt", spin: true },
    synced: { color: "text-secondary", icon: CheckCircle2, label: "fertig" },
    failed: { color: "text-destructive", icon: AlertTriangle, label: "fehler" },
    conflict: { color: "text-warning", icon: AlertTriangle, label: "konflikt" },
};

export default function OpsQueuePage() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);

    const refresh = async () => setItems(await listMine());

    useEffect(() => {
        refresh();
        const iv = setInterval(refresh, 3000);
        return () => clearInterval(iv);
    }, []);

    const retryAll = async () => {
        setBusy(true);
        await runSyncPass();
        await refresh();
        setBusy(false);
        toast.success("Sync-Lauf beendet.");
    };

    const retryOne = async (rec) => {
        setBusy(true);
        const r = await retry(rec.id);
        if (r.ok) toast.success("Ok.");
        else toast.error(r.error || "Fehler.");
        await refresh();
        setBusy(false);
    };

    const discardOne = async (rec) => {
        await discard(rec.id);
        await refresh();
    };

    const sortedItems = [...items].sort((a, b) => {
        // Unresolved first
        const order = { conflict: 0, failed: 1, pending: 2, syncing: 3, synced: 4 };
        const d = (order[a.status] ?? 9) - (order[b.status] ?? 9);
        if (d !== 0) return d;
        return b.createdAt.localeCompare(a.createdAt);
    });

    return (
        <div className="flex flex-col gap-4 px-4 pb-24 pt-4" data-testid="ops-queue-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold">Operation-Queue</h1>
                    <p className="text-xs text-muted-foreground">
                        {items.length} Einträge -- offline/online tolerante Buchungen
                    </p>
                </div>
                <Button size="sm" onClick={retryAll} disabled={busy} data-testid="ops-queue-retry-all">
                    <RefreshCw className={`mr-1 h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Jetzt syncen
                </Button>
            </div>

            {sortedItems.length === 0 ? (
                <div className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                    Keine ausstehenden Operationen.
                </div>
            ) : (
                <ul className="flex flex-col gap-2" data-testid="ops-queue-list">
                    {sortedItems.map((r) => {
                        const meta = STATUS_META[r.status] || STATUS_META.pending;
                        const Icon = meta.icon;
                        return (
                            <li
                                key={r.id}
                                className="flex items-center gap-3 rounded-md border border-border bg-card/40 px-3 py-2"
                                data-testid={`ops-queue-item-${r.id}`}
                            >
                                <Icon
                                    className={`h-5 w-5 ${meta.color} ${meta.spin ? "animate-spin" : ""}`}
                                />
                                <div className="flex-1 text-xs">
                                    <div className="font-medium text-foreground">
                                        {r.rpcName || r.path}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {meta.label} · {r.attempts || 0} Versuche
                                        {r.summary ? ` · ${r.summary}` : ""}
                                    </div>
                                    {r.lastError ? (
                                        <div className="mt-0.5 truncate text-xs text-destructive">
                                            {r.lastError}
                                        </div>
                                    ) : null}
                                </div>
                                {(r.status === "failed" || r.status === "conflict" || r.status === "pending" || r.status === "syncing") ? (
                                    <>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => retryOne(r)}
                                            disabled={busy}
                                            data-testid={`ops-queue-retry-${r.id}`}
                                        >
                                            <RefreshCw className="h-3 w-3" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => discardOne(r)}
                                            data-testid={`ops-queue-discard-${r.id}`}
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </Button>
                                    </>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
